import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { dispatch as reduxDispatch } from 'store';
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';
import { openSnackbar } from 'store/reducers/snackbar';

// project-imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { loginHandler } from 'actions/authActions';
import { getMe } from 'actions/auth';
import { clearShared } from 'utils/sharedRequest';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded = jwtDecode(serviceToken);

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
  // Shared reference data is per viewer — never carry it across sessions.
  clearShared('');
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
  } else {
    localStorage.removeItem('serviceToken');
  }
};

const setCurrentUser = (user) => {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Clear the session and surface a message (shown on the login screen
  // the router redirects to once isLoggedIn flips false).
  const endSession = (message) => {
    setSession(null);
    setCurrentUser();
    dispatch({ type: LOGOUT });
    if (message) {
      reduxDispatch(
        openSnackbar({
          open: true,
          message,
          variant: 'alert',
          alert: { color: 'error' },
          close: true,
        }),
      );
    }
  };

  useEffect(() => {
    const init = async () => {
      const serviceToken = localStorage.getItem('serviceToken');
      try {
        // No token at all → silent logout (first visit / already signed out).
        if (!serviceToken) {
          dispatch({ type: LOGOUT });
          return;
        }

        // Token present but expired/invalid locally → end with a message.
        if (!verifyToken(serviceToken)) {
          endSession('Your session has expired. Please sign in again.');
          return;
        }

        // Token looks valid — confirm with the server on every refresh.
        // Catches accounts deactivated/deleted after the token was issued.
        setSession(serviceToken);
        const me = await getMe();
        if (me.success) {
          setCurrentUser(me.data);
          dispatch({
            type: LOGIN,
            payload: { isLoggedIn: true, user: me.data },
          });
        } else {
          endSession('Your session is no longer valid. Please sign in again.');
        }
      } catch (err) {
        endSession('Your session is no longer valid. Please sign in again.');
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data, success, error } = await loginHandler({ email, password });
    const { token, user } = data?.loginUser || {};
    if (success && token) {
      setSession(token);
      setCurrentUser(user);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user,
        },
      });
      return {
        success: true,
        error: null,
      };
    } else {
      return {
        success: false,
        error,
      };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName,
    });
    let users = response.data;

    if (
      window.localStorage.getItem('users') !== undefined &&
      window.localStorage.getItem('users') !== null
    ) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`,
        },
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
    setCurrentUser();
  };

  const resetPassword = async () => {};

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node,
};

export default JWTContext;
